import React, { useState } from 'react'
import {
  SellableDetailsFragment,
  RatesTableSellableComponent,
  VariationMatrixComponent,
  VariationMatrixVectorFragment
} from '#src/generated/graphql'
import { FullBlockLoader, Button, CircleLoader } from '@deal/components'
import { formatSellablePricing } from '#src/services/sellable'
import ArrowDown from './arrow-down.svg'
import ArrowUp from './arrow-up.svg'
import styles from './styles.css'

interface RatesTableProps {
  sellable: SellableDetailsFragment
}

interface RatesTableRowProps {
  vector: VariationMatrixVectorFragment
}

const RatesTableRow: React.SFC<RatesTableRowProps> = ({ vector }) => {
  return (
    <RatesTableSellableComponent variables={{ ids: vector.sellableIds }}>
      {({ data }) => {
        if (data && data.sellables) {
          return (
            <>
              {data.sellables.map((sellable, idx) => {
                const formattedPricing = formatSellablePricing(sellable)

                return (
                  <div key={idx} className={styles.row}>
                    <div className={styles.duration}>
                      <div dangerouslySetInnerHTML={{ __html: vector.displayValueHtml }} />
                    </div>

                    <div className={styles.retailPrice}>
                      {formattedPricing && formattedPricing.msrp}
                    </div>
                    <div className={styles.pricing}>
                      {formattedPricing && formattedPricing.price}
                    </div>
                    {formattedPricing &&
                      formattedPricing.savings && (
                        <div className={styles.saving}>{formattedPricing.savings}</div>
                      )}
                  </div>
                )
              })}
            </>
          )
        }

        return null
      }}
    </RatesTableSellableComponent>
  )
}

const RatesTable: React.SFC<RatesTableProps> = ({ sellable }) => {
  const [isTableExpanded, setTableExpanded] = useState(false)

  const RATES_TABLE_THRESHOLD = 3

  if (!sellable.variationMatrix || !sellable.variationMatrix.defaultVariant) {
    return null
  }

  return (
    <VariationMatrixComponent variables={{ id: sellable.variationMatrix.defaultVariant.id }}>
      {({ data, error }) => {
        if (data && data.sellable && data.sellable.variationMatrix) {
          const variations = data.sellable.variationMatrix

          /**
           * Sellables that have a 'rates table' are keyed off a single row in the
           * variation matrix. If we expand to additional dimensions we can iterate
           * over them and draw a tab for each dimension and the sellables the
           * dimension includes. For now, this just returns the single (likely `duration`)
           * dimension.
           */
          const availableDimension = variations.dimensions[0]

          if (!availableDimension) {
            return null
          }

          return (
            <div className={styles.wrapper}>
              <div className={styles.container}>
                <div className={styles.row}>
                  <div className={styles.duration}>
                    <strong>{availableDimension.displayName}</strong>
                  </div>
                  <div className={styles.pricing}>
                    <strong>Retail price</strong>
                  </div>
                  <div className={styles.pricing}>
                    <strong>Our price</strong>
                  </div>
                  <div className={styles.pricing}>
                    <strong>You save</strong>
                  </div>
                </div>
                {availableDimension.vectors.map((vector, idx) => {
                  if (idx + 1 > RATES_TABLE_THRESHOLD && !isTableExpanded) {
                    return null
                  }

                  return <RatesTableRow key={idx} vector={vector} />
                })}
              </div>
              {availableDimension.vectors.length > RATES_TABLE_THRESHOLD && (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() =>
                    isTableExpanded ? setTableExpanded(false) : setTableExpanded(true)
                  }
                >
                  {isTableExpanded ? (
                    <>
                      <ArrowUp /> View fewer prices
                    </>
                  ) : (
                    <>
                      <ArrowDown /> View all prices
                    </>
                  )}
                </Button>
              )}
              <div className={styles.disclaimer}>* Prices and availability subject to change</div>
            </div>
          )
        } else if (error) {
          return null
        } else {
          return <FullBlockLoader loader={CircleLoader} />
        }
      }}
    </VariationMatrixComponent>
  )
}

export default RatesTable
